/* DJI Power — background video.
 *
 * Three jobs CSS cannot do:
 *
 * 1. prefers-reduced-motion. The Motion rules say every loop stops, and a
 *    looping full-bleed video is the largest motion on the site. CSS can hide
 *    it but cannot stop it autoplaying — the file still downloads and still
 *    decodes. So the markup ships WITHOUT autoplay and this adds it, only when
 *    the viewer has not asked for less motion. No JS, reduced motion, or an
 *    old browser all land on the poster, which is a finished-looking section.
 *
 * 2. Don't play what nobody is looking at. A paused offscreen video still
 *    costs decode. IntersectionObserver pauses it when it leaves.
 *
 * 3. Don't burn a phone's data plan on decoration. On a metered or explicitly
 *    saved-data connection the poster is the whole experience.
 */
(function () {
  'use strict';

  var vids = Array.prototype.slice.call(
    document.querySelectorAll('video[data-dp-autoplay]'));
  if (!vids.length) return;

  var reduced = window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var conn = navigator.connection || {};
  var thrifty = conn.saveData === true ||
    /^(slow-2g|2g)$/.test(conn.effectiveType || '');

  if (reduced || thrifty) {
    /* Leave the poster showing and never fetch the file. removeAttribute on
       the sources is not enough on its own — the element keeps the resource it
       already selected, so load() is what actually drops it. */
    vids.forEach(function (v) {
      while (v.firstChild) v.removeChild(v.firstChild);
      v.removeAttribute('src');
      v.load();
    });
    return;
  }

  function play(v) {
    var p = v.play();
    /* Safari rejects autoplay in Low Power Mode. That is a refusal, not an
       error: the poster stays and the page is fine, so swallow it rather than
       letting it surface as an unhandled rejection. */
    if (p && p.catch) p.catch(function () {});
  }

  if (!('IntersectionObserver' in window)) {
    vids.forEach(play);
    return;
  }

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) play(e.target);
      else if (!e.target.paused) e.target.pause();
    });
  }, { rootMargin: '200px 0px' });

  vids.forEach(function (v) { io.observe(v); });
}());

/* DJI Power — launch films.
 *
 * Two modes, chosen by data-dp-films-autoplay on the section:
 *
 *   click   — the poster is a link; the player is built when it is clicked.
 *   autoplay — the player is built when the film nears the viewport and plays
 *              muted, on loop, with YouTube's chrome hidden.
 *
 * In BOTH modes nothing is requested from Google on page load, and the shipped
 * HTML contains no iframe. Autoplay moves the moment of contact from "clicked"
 * to "scrolled near", not to "opened the page" — a visitor who never reaches
 * the section still never reaches YouTube. Audit Y1 depends on this.
 *
 * Reduced motion or a saved-data connection falls back to click, because
 * autoplaying a looping film is exactly what those settings ask us not to do.
 *
 * No JS at all: the poster stays a plain link to the film on YouTube.
 */
(function () {
  'use strict';

  var ID_OK = /^[A-Za-z0-9_-]{6,20}$/;
  var HOST = 'https://www.youtube-nocookie.com';

  function build(trigger, opts) {
    var id = trigger.getAttribute('data-dp-film');
    if (!ID_OK.test(id)) return null;          // never build a src from junk

    var q = '?rel=0&modestbranding=1&playsinline=1&enablejsapi=1';
    if (opts.loop) {
      /* A single video needs playlist=<its own id> for loop to do anything —
         loop alone is defined against a playlist and is silently ignored. */
      q += '&autoplay=1&mute=1&loop=1&playlist=' + id + '&controls=0';
    } else {
      q += '&autoplay=1';
    }

    var frame = document.createElement('iframe');
    frame.className = 'dp-film__frame';
    frame.src = HOST + '/embed/' + id + q;
    frame.title = trigger.getAttribute('data-dp-title') || 'DJI launch film';
    frame.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; ' +
                  'picture-in-picture; web-share';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
    if (opts.loop) {
      frame.setAttribute('tabindex', '-1');  // chrome is hidden
      frame.className += ' dp-film__frame--loop';
    }
    trigger.parentNode.replaceChild(frame, trigger);
    return frame;
  }

  /* Drive the player without loading YouTube's API script: the embed accepts
     these commands on postMessage once enablejsapi=1 is set. */
  function command(frame, func) {
    try {
      frame.contentWindow.postMessage(
        JSON.stringify({ event: 'command', func: func, args: [] }), HOST);
    } catch (e) { /* not ready yet; the toggle simply no-ops */ }
  }

  var section = document.querySelector('[data-dp-films-autoplay]');
  var autoplay = !!section &&
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    !(navigator.connection || {}).saveData &&
    !/^(slow-2g|2g)$/.test((navigator.connection || {}).effectiveType || '');

  /* ---- click mode ---- */
  document.addEventListener('click', function (e) {
    var trigger = e.target.closest && e.target.closest('[data-dp-film]');
    if (!trigger) return;
    e.preventDefault();
    var f = build(trigger, { loop: false });
    if (f) f.focus();
  });

  /* ---- autoplay mode ---- */
  if (!autoplay || !('IntersectionObserver' in window)) return;

  var io = new IntersectionObserver(function (entries) {
    entries.forEach(function (entry) {
      if (!entry.isIntersecting) return;
      var trigger = entry.target;
      io.unobserve(trigger);
      var stage = trigger.parentNode;
      var frame = build(trigger, { loop: true });
      if (!frame) return;

      /* WCAG 2.2 SC 2.2.2: content that moves automatically for more than
         five seconds must be pausable. YouTube's own controls are hidden for
         the ambient look, so the page provides the control itself. */
      var btn = stage.querySelector('.dp-film__pause');
      if (!btn) return;
      btn.hidden = false;
      function show(playing) {
        btn.setAttribute('aria-pressed', playing ? 'false' : 'true');
        btn.textContent = playing ? 'Pause' : 'Play';
      }
      btn.addEventListener('click', function () {
        var playing = btn.getAttribute('aria-pressed') === 'false';
        command(frame, playing ? 'pauseVideo' : 'playVideo');
        show(!playing);
      });
      /* The toggle follows the PLAYER, not our guess. The frame takes no
         taps (its chrome is cropped away), so when autoplay is refused, as
         iOS Low Power Mode refuses even muted video, YouTube's own play
         button is unreachable and a toggle stuck on "Pause" would be the
         only control, and wrong. With enablejsapi the embed reports its
         playerState once told someone is listening; until it says it is
         playing, the toggle offers Play. */
      players.push({ frame: frame, show: show });
      show(false);
      frame.addEventListener('load', function () {
        try {
          frame.contentWindow.postMessage(JSON.stringify(
            { event: 'listening', id: frame.src, channel: 'widget' }), HOST);
        } catch (e) { /* the toggle still works by command */ }
      });
    });
  }, { rootMargin: '200px 0px' });

  var players = [];
  window.addEventListener('message', function (e) {
    if (e.origin !== HOST || typeof e.data !== 'string') return;
    var d;
    try { d = JSON.parse(e.data); } catch (x) { return; }
    var st = d && d.info && d.info.playerState;
    if (typeof st !== 'number') return;
    for (var i = 0; i < players.length; i++) {
      if (players[i].frame.contentWindow === e.source) {
        // 1 playing, 3 buffering: both are "running" to the viewer.
        players[i].show(st === 1 || st === 3);
      }
    }
  });

  document.querySelectorAll('[data-dp-film]').forEach(function (a) {
    io.observe(a);
  });
}());
